import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { useTranslations } from "../../contexts/Translation";
import { H2 } from "../Headers/Headings";
import Button from "../Button/Button";
import Card from "../Card/Card";
import Input from "../Forms/Input";
import { InfoMessage } from "../InfoMessage/InfoMessage";
import { useApiData } from "../../contexts/ApiData";
import type { Profile } from "../../types";
import styles from "./DataPrivacy.module.css";
import { homepageUrl } from "../../lib/runtimeConfig";

const normalize = (value: string | undefined | null) => (value ?? "").trim().toLowerCase();

type DataPrivacyProps = {
  profile: Profile;
};

export const DataPrivacy = ({ profile }: DataPrivacyProps) => {
  const t = useTranslations("dataPrivacy");
  const { download, del } = useApiData();
  const { logout } = useAuth();

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const accountEmail = profile.email;
  // Without an email on file we cannot run the type-your-email confirmation gate — point the user to
  // support instead of offering a flow the server will reject.
  const canDelete = !!normalize(accountEmail);
  const confirmMatches = canDelete && normalize(confirmEmail) === normalize(accountEmail);

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    const filename = `feedbackjourney-data-export-${new Date().toISOString().slice(0, 10)}.json`;
    const ok = await download("/account/export", filename);
    if (!ok) setExportError(t("exportError"));
    setExporting(false);
  };

  const handleDelete = async () => {
    if (!confirmMatches || deleting) return;
    if (!window.confirm(t("finalConfirm"))) return;

    setDeleteError(null);
    setDeleting(true);
    const result = await del("/account", { confirmEmail });
    if (result && result.ok) {
      // Account is gone; end the login session and return to the public front page.
      logout(homepageUrl());
      return;
    }
    setDeleteError(t("deleteError"));
    setDeleting(false);
  };

  return (
    <div className={styles.wrapper}>
      <section>
        <H2>{t("exportHeading")}</H2>
        <p className={styles.description}>{t("exportDescription")}</p>
        {exportError && <InfoMessage type='warning'>{exportError}</InfoMessage>}
        <Button type='button' onClick={handleExport} disabled={exporting} testId='export-data'>
          {exporting ? t("exporting") : t("exportButton")}
        </Button>
      </section>

      <Card className={styles.dangerZone} padding='large'>
        <H2>{t("deleteHeading")}</H2>
        <p className={styles.description}>{t("deleteDescription")}</p>
        <p className={styles.note}>{t("deleteRetentionNote")}</p>
        <p className={styles.note}>{t("deleteOrgNote")}</p>

        {!canDelete ? (
          <InfoMessage type='warning'>{t("noEmailError")}</InfoMessage>
        ) : (
          <>
            {deleteError && <InfoMessage type='warning'>{deleteError}</InfoMessage>}
            <Input
              name='confirmEmail'
              type='email'
              label={t("confirmLabel", accountEmail)}
              value={confirmEmail}
              handleChange={(e) => {
                setDeleteError(null);
                setConfirmEmail(e.target.value);
              }}
            />
            <Button
              type='button'
              variant='secondary'
              className={styles.deleteButton}
              onClick={handleDelete}
              disabled={!confirmMatches || deleting}
              testId='delete-account'
            >
              {deleting ? t("deleting") : t("deleteButton")}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default DataPrivacy;
